import { useEffect, useState } from "react";
import api, { fmtError, API, openPdfBlob } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FileText, Plus, Trash, PaperPlaneTilt, CheckCircle, Clock, ArrowLeft } from "@phosphor-icons/react";
import { PdfPageViewer } from "./PdfPageViewer";
import { formatDate } from "../../lib/date";

const STATUS_META = {
  draft: { label: "Draft — not sent", cls: "bg-neutral-100 text-neutral-600" },
  pending_signatures: { label: "Awaiting signatures", cls: "bg-amber-50 text-amber-700" },
  completed: { label: "Fully signed", cls: "bg-emerald-50 text-emerald-700" },
};

const ROLE_OPTIONS = [
  { value: "business", label: "Business" },
  { value: "oodel_ceo", label: "oodel CEO / Admin" },
  { value: "other", label: "Other" },
];

function authedFile(path) {
  const token = localStorage.getItem("pollpak_token");
  return { url: `${API}${path}`, httpHeaders: token ? { Authorization: `Bearer ${token}` } : {} };
}

function UploadModal({ businessId, open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("Pilot Proposal");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !file) { toast.error("Title and a PDF file are required."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/admin/documents?business_id=${businessId}&title=${encodeURIComponent(title)}&doc_type=${encodeURIComponent(docType)}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — now add signers and place their signature boxes.");
      onCreated(r.data);
      setTitle(""); setFile(null);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle>Upload a document for signature</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title, e.g. Pilot Proposal — Eatology" className="rounded-none" data-testid="doc-upload-title" />
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="rounded-none" data-testid="doc-upload-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Pilot Proposal">Pilot Proposal</SelectItem>
              <SelectItem value="Commercial Agreement">Commercial Agreement</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-upload-file" className="text-sm" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-mango hover:bg-mango/90" data-testid="doc-upload-submit">{busy ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigureView({ doc, onBack, onSent }) {
  const [signers, setSigners] = useState(doc.signers.length ? doc.signers : [{ order: 1, role: "business", name: "", email: "", box: null }]);
  const [armedOrder, setArmedOrder] = useState(null);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const addSigner = () => setSigners([...signers, { order: signers.length + 1, role: "oodel_ceo", name: "", email: "", box: null }]);
  const removeSigner = (order) => setSigners(signers.filter((s) => s.order !== order).map((s, i) => ({ ...s, order: i + 1 })));
  const updateSigner = (order, patch) => setSigners(signers.map((s) => (s.order === order ? { ...s, ...patch } : s)));

  const placeBox = ({ x_pct, y_pct }) => {
    if (armedOrder == null) return;
    const box = { page: page - 1, x_pct: Math.max(0, Math.min(0.72, x_pct)), y_pct: Math.max(0, Math.min(0.9, y_pct)), w_pct: 0.28, h_pct: 0.08 };
    updateSigner(armedOrder, { box });
    setArmedOrder(null);
  };

  const save = async () => {
    if (signers.some((s) => !s.name.trim() || !s.email.trim())) { toast.error("Every signer needs a name and email."); return; }
    setSaving(true);
    try {
      await api.put(`/admin/documents/${doc.id}/signers`, { signers: signers.map((s) => ({ order: s.order, role: s.role, name: s.name, email: s.email, box: s.box })) });
      toast.success("Signers saved.");
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const send = async () => {
    if (signers.some((s) => !s.box)) { toast.error("Place a signature box for every signer first."); return; }
    setSending(true);
    try {
      await save();
      await api.post(`/admin/documents/${doc.id}/send`);
      toast.success(`Sent to ${signers[0].name} for the first signature.`);
      onSent();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  return (
    <div data-testid="doc-configure-view">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-black mb-3"><ArrowLeft size={14} /> Back to documents</button>
      <div className="grid md:grid-cols-[1fr_320px] gap-6">
        <div>
          <div className="text-xs text-neutral-400 mb-2">Click on the page to drop the ARMED signer's box (page {page} of {doc.page_count}).</div>
          <div className="flex gap-2 mb-2">
            {Array.from({ length: doc.page_count }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} className={`w-7 h-7 text-xs border ${p === page ? "bg-black text-white" : "bg-white"}`} data-testid={`doc-page-select-${p}`}>{p}</button>
            ))}
          </div>
          <PdfPageViewer
            file={authedFile(`/admin/documents/${doc.id}/pdf`)}
            pageNumber={page}
            onBoxClick={armedOrder != null ? placeBox : undefined}
            overlays={signers.filter((s) => s.box && s.box.page === page - 1).map((s) => (
              <div key={s.order} className="absolute border-2 border-mango bg-mango/10 flex items-center justify-center text-[10px] font-bold text-mango pointer-events-none"
                style={{ left: `${s.box.x_pct * 100}%`, top: `${s.box.y_pct * 100}%`, width: `${s.box.w_pct * 100}%`, height: `${s.box.h_pct * 100}%` }}>
                {s.name || `Signer ${s.order}`}
              </div>
            ))}
          />
        </div>
        <div className="space-y-4">
          {signers.map((s) => (
            <div key={s.order} className="border-2 border-black p-3" data-testid={`doc-signer-row-${s.order}`}>
              <div className="text-xs font-bold mb-2">Signer {s.order}</div>
              <Input value={s.name} onChange={(e) => updateSigner(s.order, { name: e.target.value })} placeholder="Full name" className="rounded-none mb-2" data-testid={`doc-signer-name-${s.order}`} />
              <Input value={s.email} onChange={(e) => updateSigner(s.order, { email: e.target.value })} placeholder="Email" className="rounded-none mb-2" data-testid={`doc-signer-email-${s.order}`} />
              <Select value={s.role} onValueChange={(v) => updateSigner(s.order, { role: v })}>
                <SelectTrigger className="rounded-none mb-2" data-testid={`doc-signer-role-${s.order}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex items-center justify-between">
                <button onClick={() => setArmedOrder(s.order)} className={`text-xs underline ${armedOrder === s.order ? "text-mango font-bold" : "text-neutral-500"}`} data-testid={`doc-signer-place-box-${s.order}`}>
                  {armedOrder === s.order ? "Click the PDF to place…" : s.box ? "Re-place signature box" : "Place signature box"}
                </button>
                {signers.length > 1 && <button onClick={() => removeSigner(s.order)} data-testid={`doc-signer-remove-${s.order}`}><Trash size={14} className="text-red-500" /></button>}
              </div>
              {s.box && <div className="text-[10px] text-neutral-400 mt-1">Box on page {s.box.page + 1} ✓</div>}
            </div>
          ))}
          <button onClick={addSigner} className="text-xs underline text-mango flex items-center gap-1" data-testid="doc-signer-add"><Plus size={12} /> Add another signer</button>
          <div className="flex flex-col gap-2 pt-2 border-t">
            <Button onClick={save} disabled={saving} variant="outline" className="rounded-none" data-testid="doc-signers-save">{saving ? "Saving…" : "Save signers"}</Button>
            <Button onClick={send} disabled={sending} className="rounded-none bg-mango hover:bg-mango/90" data-testid="doc-send-for-signature"><PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Send for signature"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminDocumentsPanel({ businessId }) {
  const [docs, setDocs] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [configuring, setConfiguring] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => { api.get(`/admin/documents`, { params: { business_id: businessId } }).then((r) => setDocs(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [businessId]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/documents/${deleteTarget.id}`);
      toast.success("Document deleted.");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setDeleting(false); }
  };

  if (configuring) {
    return <ConfigureView doc={configuring} onBack={() => { setConfiguring(null); load(); }} onSent={() => { setConfiguring(null); load(); }} />;
  }

  return (
    <div className="mb-8" data-testid="admin-documents-panel">
      <div className="overline mb-3 flex items-center gap-2"><FileText size={16} /> DOCUMENTS &amp; SIGNATURES</div>
      <p className="text-xs text-neutral-500 mb-3">Upload a pilot proposal, commercial agreement, or any other document that needs the business and oodel to both sign — in order.</p>
      <div className="space-y-2 mb-3">
        {docs.map((d) => {
          const meta = STATUS_META[d.status];
          return (
            <div key={d.id} className="border-2 border-black p-3 flex items-center justify-between" data-testid={`doc-row-${d.id}`}>
              <div>
                <div className="font-bold text-sm">{d.title} <span className="text-xs text-neutral-400 font-normal">({d.doc_type})</span></div>
                <div className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 mt-1 ${meta.cls}`}>{meta.label}</div>
                {d.signers.length > 0 && (
                  <div className="flex gap-2 mt-1">
                    {[...d.signers].sort((a, b) => a.order - b.order).map((s) => (
                      <span key={s.order} className="text-[10px] flex items-center gap-1 text-neutral-500">
                        {s.status === "signed" ? <CheckCircle size={11} className="text-emerald-600" /> : <Clock size={11} />} {s.name || `Signer ${s.order}`}
                      </span>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-neutral-400 mt-1">Created {formatDate(d.created_at)}</div>
              </div>
              <div className="flex gap-3 shrink-0">
                {d.status === "draft" && <button onClick={() => setConfiguring(d)} className="text-xs underline text-mango font-bold" data-testid={`doc-configure-${d.id}`}>Configure</button>}
                {d.status !== "draft" && <button onClick={() => openPdfBlob(`/admin/documents/${d.id}/pdf`, `${d.title}.pdf`).catch(() => toast.error("Couldn't open this document."))} className="text-xs underline text-neutral-600" data-testid={`doc-view-${d.id}`}>View PDF</button>}
                <button onClick={() => setDeleteTarget(d)} className="text-xs text-red-600 underline" data-testid={`doc-delete-${d.id}`}>Delete</button>
              </div>
            </div>
          );
        })}
        {docs.length === 0 && <div className="text-sm text-neutral-400">No documents yet.</div>}
      </div>
      <Button onClick={() => setUploadOpen(true)} variant="outline" className="rounded-none" data-testid="doc-upload-open"><Plus size={14} className="mr-1" /> Upload new document</Button>
      <UploadModal businessId={businessId} open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={(d) => { setUploadOpen(false); load(); setConfiguring(d); }} />

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md rounded-none" data-testid="doc-delete-confirm-dialog">
          <DialogHeader><DialogTitle>Delete "{deleteTarget?.title}"?</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-600">
            {deleteTarget && deleteTarget.status !== "draft"
              ? "This document has already been sent and " + (deleteTarget.status === "completed" ? "fully signed. " : "is awaiting signatures. ") +
                "Deleting it permanently removes the document, its signed copy, and all signer records — this cannot be undone and it will also disappear from the business's own Documents tab."
              : "This will permanently delete this draft document. This cannot be undone."}
          </p>
          <DialogFooter>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting} variant="outline" className="rounded-none" data-testid="doc-delete-cancel">Cancel</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="doc-delete-confirm">{deleting ? "Deleting…" : "Delete permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
